import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Camera, X, Thermometer, Clock, Cloud, ExternalLink, Car, Users, Play, Mail } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { NewsletterModal } from './NewsletterModal';

const WEBCAM_URL = `${process.env.REACT_APP_BACKEND_URL}/api/webcam`;

export const WebcamFloatingIcon = () => {
  const { language } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [isNewsletterOpen, setIsNewsletterOpen] = useState(false);
  const [imageKey, setImageKey] = useState(Date.now());
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [temperature, setTemperature] = useState(null);
  const [weatherDesc, setWeatherDesc] = useState('');

  const texts = {
    ca: {
      label: 'El camp ara',
      traffic: 'El trànsit ara',
      members: 'Zona membres',
      video: 'Vola sobre Aravell',
      live: 'En directe',
      updated: 'Actualitzat fa',
      minutes: 'min',
      forecast: 'Previsió',
      openNew: 'Obrir en nova finestra'
    },
    es: {
      label: 'El campo ahora',
      traffic: 'El tráfico ahora',
      members: 'Zona miembros',
      video: 'Vuela sobre Aravell',
      live: 'En directo',
      updated: 'Actualizado hace',
      minutes: 'min',
      forecast: 'Previsión',
      openNew: 'Abrir en nueva ventana'
    },
    fr: {
      label: 'Le parcours',
      traffic: 'Le trafic',
      members: 'Espace membres',
      video: 'Survolez Aravell',
      live: 'En direct',
      updated: 'Mis à jour',
      minutes: 'min',
      forecast: 'Prévisions',
      openNew: 'Ouvrir dans une nouvelle fenêtre'
    },
    en: {
      label: 'Course now',
      traffic: 'Traffic now',
      members: 'Members area',
      video: 'Fly over Aravell',
      live: 'Live',
      updated: 'Updated',
      minutes: 'min ago',
      forecast: 'Forecast',
      openNew: 'Open in new window'
    }
  };

  const t = texts[language] || texts.ca;

  // Fetch weather
  const fetchWeather = useCallback(async () => {
    try {
      // Aravell Golf Club coordinates: 42.3605°N, 1.4053°E
      const response = await fetch(
        'https://api.open-meteo.com/v1/forecast?latitude=42.3605&longitude=1.4053&current=temperature_2m,weather_code&timezone=Europe%2FMadrid'
      );
      const data = await response.json();
      if (data.current) {
        setTemperature(Math.round(data.current.temperature_2m));
        const code = data.current.weather_code;
        if (code === 0) setWeatherDesc(language === 'ca' ? 'Cel clar' : language === 'es' ? 'Despejado' : language === 'fr' ? 'Dégagé' : 'Clear');
        else if (code <= 3) setWeatherDesc(language === 'ca' ? 'Nuvolositat variable' : language === 'es' ? 'Nubes' : language === 'fr' ? 'Nuageux' : 'Cloudy');
        else setWeatherDesc(language === 'ca' ? 'Variable' : 'Variable');
      }
    } catch (error) {
      console.error('Weather fetch error:', error);
    }
  }, [language]);

  useEffect(() => {
    fetchWeather();
  }, [fetchWeather]);

  const refreshImage = () => {
    setImageKey(Date.now());
    setLastUpdate(new Date());
    fetchWeather();
  };

  const getMinutesSinceUpdate = () => {
    const now = new Date();
    const diff = Math.floor((now - lastUpdate) / 60000);
    return diff < 1 ? '< 1' : diff;
  };

  const handleOpen = () => {
    refreshImage();
    setIsOpen(true);
  };

  return (
    <>
      {/* Floating Buttons Group - Stacked vertically */}
      <motion.div
        className="absolute top-24 right-4 md:top-28 md:right-8 z-30 flex flex-col gap-1"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1, duration: 0.5 }}
      >
        {/* EL CAMP ARA - Webcam Button */}
        <button
          onClick={handleOpen}
          className="flex items-center gap-2 bg-black/40 hover:bg-black/60 backdrop-blur-md px-4 py-2.5 text-white transition-all duration-300 group"
          data-testid="webcam-floating-btn"
        >
          <div className="relative">
            <Camera className="w-5 h-5" />
            <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse" />
          </div>
          <span className="text-xs font-medium tracking-wide uppercase">{t.label}</span>
          {temperature !== null && (
            <span className="text-xs font-semibold ml-1 opacity-80">{temperature}°</span>
          )}
        </button>

        {/* EL TRÀNSIT ARA - Traffic Button */}
        <a
          href="https://www.meteoblue.com/en/weather/webcams/la-seu-d%27urgell_spain_3109143"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 bg-black/40 hover:bg-black/60 backdrop-blur-md px-4 py-2.5 text-white transition-all duration-300 group"
          data-testid="traffic-floating-btn"
        >
          <Car className="w-5 h-5" />
          <span className="text-xs font-medium tracking-wide uppercase">{t.traffic}</span>
        </a>

        {/* ZONA MEMBRES - Members Button - Now goes to Fes-te Abonat page */}
        <a
          href="/fes-te-abonat"
          className="flex items-center gap-2 bg-black/40 hover:bg-black/60 backdrop-blur-md px-4 py-2.5 text-white transition-all duration-300 group"
          data-testid="members-floating-btn"
        >
          <Users className="w-5 h-5" />
          <span className="text-xs font-medium tracking-wide uppercase">{t.members}</span>
        </a>

        {/* NEWSLETTER - Subscribe Button */}
        <button
          onClick={() => setIsNewsletterOpen(true)}
          className="flex items-center gap-2 bg-black/40 hover:bg-black/60 backdrop-blur-md px-4 py-2.5 text-white transition-all duration-300 group"
          data-testid="newsletter-floating-btn"
        >
          <Mail className="w-5 h-5" />
          <span className="text-xs font-medium tracking-wide uppercase">Newsletter</span>
        </button>

        {/* VOLA SOBRE ARAVELL - Video Button */}
        <button
          onClick={() => setIsVideoOpen(true)}
          className="flex items-center gap-2 bg-[#1A4D2E]/90 hover:bg-[#1A4D2E] backdrop-blur-md px-4 py-2.5 text-white transition-all duration-300 group"
          data-testid="video-floating-btn"
        >
          <Play className="w-5 h-5 fill-current" />
          <span className="text-xs font-medium tracking-wide uppercase">{t.video}</span>
        </button>
      </motion.div>

      {/* Lightbox Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setIsOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.3 }}
              className="relative w-full max-w-4xl bg-white overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E5E5] bg-white">
                <div className="flex items-center gap-3">
                  <span className="w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse" />
                  <span className="text-sm font-semibold tracking-wide uppercase text-[#1A4D2E]">
                    {t.live}
                  </span>
                  <span className="text-sm text-[#666666]">UGOLF ARAVELL Andorra</span>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 hover:bg-[#F0F2EF] rounded-full transition-colors duration-200"
                  aria-label="Close"
                >
                  <X className="w-5 h-5 text-[#666666]" />
                </button>
              </div>

              {/* Image */}
              <div className="relative bg-[#1A1A1A]">
                <img
                  src={`${WEBCAM_URL}?t=${imageKey}`}
                  alt="UGOLF ARAVELL Andorra - Live"
                  className="w-full h-auto max-h-[60vh] object-contain"
                />
              </div>

              {/* Info Footer */}
              <div className="px-5 py-4 bg-[#F9F9F7] flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-5">
                  {temperature !== null && (
                    <div className="flex items-center gap-2">
                      <Thermometer className="w-5 h-5 text-[#1A4D2E]" />
                      <span className="text-xl font-semibold text-[#1A1A1A]">{temperature}°C</span>
                    </div>
                  )}
                  {weatherDesc && (
                    <div className="flex items-center gap-2 text-[#666666]">
                      <Cloud className="w-4 h-4" />
                      <span className="text-sm">{weatherDesc}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-[#666666]">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm">{t.updated} {getMinutesSinceUpdate()} {t.minutes}</span>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <a
                    href={`${WEBCAM_URL}?t=${imageKey}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 text-xs font-medium text-[#666666] hover:text-[#1A4D2E] border border-[#E5E5E5] hover:border-[#1A4D2E] transition-colors duration-200"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    {t.openNew}
                  </a>
                  <a
                    href="https://www.meteoblue.com/en/weather/week/la-seu-d'urgell_spain_3109143"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 bg-[#1A4D2E] text-white px-5 py-2 text-xs font-semibold tracking-wide uppercase hover:bg-[#153d24] transition-colors duration-200"
                  >
                    <Cloud className="w-4 h-4" />
                    {t.forecast}
                  </a>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Video Modal */}
      <AnimatePresence>
        {isVideoOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4"
            onClick={() => setIsVideoOpen(false)}
            data-testid="video-modal"
          >
            <button
              onClick={() => setIsVideoOpen(false)}
              className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10"
              aria-label="Tancar"
            >
              <X className="w-8 h-8" />
            </button>

            <motion.p
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="absolute top-4 left-4 text-white text-lg font-semibold"
            >
              {t.video}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="w-full max-w-5xl"
              onClick={(e) => e.stopPropagation()}
            >
              <video
                src="https://customer-assets.emergentagent.com/job_6551900b-6eea-49b4-a7ed-baa10c533f6e/artifacts/fk1eq9f3_flyoverAravellGolf.mp4"
                controls
                autoPlay
                className="w-full rounded-lg shadow-2xl"
                style={{ maxHeight: '80vh' }}
              >
                El teu navegador no suporta vídeos HTML5.
              </video>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Newsletter Modal */}
      <NewsletterModal isOpen={isNewsletterOpen} onClose={() => setIsNewsletterOpen(false)} />
    </>
  );
};

export default WebcamFloatingIcon;
